import { create } from 'zustand';

import { resizableDockWidth } from '~/ui/domain/tiling-engine/tile-resize';

import type { DiffEditTarget } from '../../data-contract/chat';
import { CHAT_THREADS_RAIL_WIDTH, CHAT_TILE_ID, CHAT_TILE_WIDTH, CHAT_TILE_WIDTH_WIDE } from '../constants';

/** One pending inline edit to a change proposal, kept with its structured target so the
 * resume payload is built from discriminated fields. The record key is derived from the
 * target via editKeyForTarget purely for uniqueness and is never parsed back. */
export type ProposalEdit = {
    readonly target: DiffEditTarget;
    readonly value: string;
};

export type ChatOpenState = {
    readonly isOpen: boolean;
    /** Whether the dock is widened by any chat affordance that needs more horizontal room. */
    readonly isWide: boolean;
    /** Whether the threads rail is open beside the conversation. */
    readonly isThreadsRailOpen: boolean;
    /** Identifiers of change-proposal sections currently in compare mode. */
    readonly compareIds: readonly string[];
    /** Whether a Markdown table answer has manually requested the widened dock. */
    readonly isMarkdownTableWide: boolean;
    /**
     * Pending question answers selected in the composer but not yet submitted, keyed by
     * blockId then actionId. Shared so the thread can show each answered question (and reveal
     * the next one) while the composer asks the current question, before the backend echoes
     * the answered block back.
     */
    readonly blockAnswers: Readonly<Record<string, Readonly<Record<string, string>>>>;
    /**
     * Pending inline edits to a change proposal's drafted content, keyed by blockId then
     * a per-target edit key. Held here because the diff card (thread) and the accept/reject
     * question (composer) live in separate trees; on submit the edits are sent as an
     * edited-content action of the answer (the backend only applies them alongside an accept).
     */
    readonly blockEdits: Readonly<Record<string, Readonly<Record<string, ProposalEdit>>>>;
    /** Blocks with a section editor currently open. While set, answering the proposal is
     * blocked (the pending text is neither saved nor discarded) and attention is requested. */
    readonly blockEditing: Readonly<Record<string, boolean>>;
    /** Per-block counter bumped whenever a blocked action should draw the eye to the open
     * editor (shake + scroll); consumers react to the value changing. */
    readonly editAttention: Readonly<Record<string, number>>;
    /** Attachments deleted this session; the thread shows them deleted before its history reloads. */
    readonly deletedAttachmentIds: ReadonlySet<string>;
    readonly markAttachmentDeleted: (attachmentId: string) => void;
    readonly close: () => void;
    readonly open: () => void;
    readonly toggle: () => void;
    readonly setMarkdownTableWide: (wide: boolean) => void;
    readonly setWide: (wide: boolean) => void;
    readonly closeThreadsRail: () => void;
    readonly toggleThreadsRail: () => void;
    readonly addCompareId: (compareId: string) => void;
    readonly removeCompareId: (compareId: string) => void;
    readonly clearCompareIdsByPrefix: (prefix: string) => void;
    readonly setBlockAnswer: (blockId: string, actionId: string, answer: string) => void;
    /** Drops the pending answers of a block whose submission was rejected, so it can be answered again. */
    readonly clearBlockAnswers: (blockId: string) => void;
    readonly setBlockEdit: (blockId: string, editKey: string, edit: ProposalEdit) => void;
    readonly removeBlockEdit: (blockId: string, editKey: string) => void;
    readonly clearBlockEdits: (blockId: string) => void;
    readonly setBlockEditing: (blockId: string, editing: boolean) => void;
    readonly requestEditAttention: (blockId: string) => void;
};

const shouldUseWideDock = (compareIds: readonly string[], isMarkdownTableWide: boolean): boolean =>
    compareIds.length > 0 || isMarkdownTableWide;

export const useChatStore = create<ChatOpenState>((set) => ({
    close: () =>
        set((state) => {
            if (!state.isOpen) {
                return state;
            }

            // Closing the chat also drops widened dock requests, any active compare and the threads rail.
            return {
                blockAnswers: {},
                blockEdits: {},
                blockEditing: {},
                compareIds: [],
                editAttention: {},
                isMarkdownTableWide: false,
                isOpen: false,
                isThreadsRailOpen: false,
                isWide: false,
            };
        }),
    blockAnswers: {},
    blockEdits: {},
    blockEditing: {},
    editAttention: {},
    compareIds: [],
    deletedAttachmentIds: new Set(),
    markAttachmentDeleted: (attachmentId) =>
        set((state) => ({ deletedAttachmentIds: new Set([...state.deletedAttachmentIds, attachmentId]) })),
    isMarkdownTableWide: false,
    isOpen: false,
    isThreadsRailOpen: false,
    isWide: false,
    closeThreadsRail: () => set((state) => (state.isThreadsRailOpen ? { isThreadsRailOpen: false } : state)),
    toggleThreadsRail: () => set((state) => ({ isThreadsRailOpen: !state.isThreadsRailOpen })),
    open: () =>
        set((state) => {
            if (state.isOpen) {
                return state;
            }

            return { isOpen: true };
        }),
    // Entering compare widens the dock for side-by-side views; leaving all compares narrows back.
    addCompareId: (compareId) =>
        set((state) => {
            if (state.compareIds.includes(compareId)) return state;

            const compareIds = [...state.compareIds, compareId];
            return {
                compareIds,
                isWide: shouldUseWideDock(compareIds, state.isMarkdownTableWide),
            };
        }),
    clearCompareIdsByPrefix: (prefix) =>
        set((state) => {
            const compareIds = state.compareIds.filter((compareId) => !compareId.startsWith(prefix));
            if (compareIds.length === state.compareIds.length) return state;

            return {
                compareIds,
                isWide: shouldUseWideDock(compareIds, state.isMarkdownTableWide),
            };
        }),
    removeCompareId: (compareId) =>
        set((state) => {
            if (!state.compareIds.includes(compareId)) return state;

            const compareIds = state.compareIds.filter((id) => id !== compareId);
            return {
                compareIds,
                isWide: shouldUseWideDock(compareIds, state.isMarkdownTableWide),
            };
        }),
    setBlockAnswer: (blockId, actionId, answer) =>
        set((state) => ({
            blockAnswers: {
                ...state.blockAnswers,
                [blockId]: { ...state.blockAnswers[blockId], [actionId]: answer },
            },
        })),
    clearBlockAnswers: (blockId) =>
        set((state) => {
            if (!(blockId in state.blockAnswers)) return state;

            const { [blockId]: _removed, ...blockAnswers } = state.blockAnswers;
            return { blockAnswers };
        }),
    clearBlockEdits: (blockId) =>
        set((state) => {
            if (!(blockId in state.blockEdits)) return state;

            const { [blockId]: _removed, ...blockEdits } = state.blockEdits;
            return { blockEdits };
        }),
    removeBlockEdit: (blockId, editKey) =>
        set((state) => {
            const edits = state.blockEdits[blockId];
            if (edits === undefined || !(editKey in edits)) return state;

            const { [editKey]: _removed, ...remaining } = edits;
            if (Object.keys(remaining).length === 0) {
                const { [blockId]: _removedBlock, ...blockEdits } = state.blockEdits;
                return { blockEdits };
            }

            return { blockEdits: { ...state.blockEdits, [blockId]: remaining } };
        }),
    setBlockEdit: (blockId, editKey, edit) =>
        set((state) => ({
            blockEdits: {
                ...state.blockEdits,
                [blockId]: { ...state.blockEdits[blockId], [editKey]: edit },
            },
        })),
    setBlockEditing: (blockId, editing) =>
        set((state) => {
            if ((state.blockEditing[blockId] === true) === editing) return state;
            if (editing) {
                return { blockEditing: { ...state.blockEditing, [blockId]: true } };
            }

            const { [blockId]: _removed, ...blockEditing } = state.blockEditing;
            return { blockEditing };
        }),
    requestEditAttention: (blockId) =>
        set((state) => ({
            editAttention: {
                ...state.editAttention,
                [blockId]: (state.editAttention[blockId] ?? 0) + 1,
            },
        })),
    setMarkdownTableWide: (wide) =>
        set((state) =>
            state.isMarkdownTableWide === wide
                ? state
                : {
                      isMarkdownTableWide: wide,
                      isWide: shouldUseWideDock(state.compareIds, wide),
                  },
        ),
    setWide: (wide) =>
        set((state) =>
            state.isWide === wide
                ? state
                : {
                      isWide: wide,
                  },
        ),
    toggle: () => set((state) => ({ isOpen: !state.isOpen })),
}));

export const useChatOpen = (): boolean => useChatStore((state) => state.isOpen);

/** Width of the conversation column alone, without the threads rail. */
export const useChatConversationWidth = (): string =>
    useChatStore((state) => (state.isWide ? CHAT_TILE_WIDTH_WIDE : CHAT_TILE_WIDTH));

/**
 * Width of the whole chat dock: the conversation, plus the threads rail and one tile gap while it is open.
 * A width the user dragged the dock to wins when it is wider.
 */
export const useChatWidth = (): string => {
    const conversationWidth = useChatConversationWidth();
    const isThreadsRailOpen = useChatStore((state) => state.isThreadsRailOpen);
    const contentWidth = isThreadsRailOpen
        ? `calc(${conversationWidth} + ${CHAT_THREADS_RAIL_WIDTH} + var(--spacing-m))`
        : conversationWidth;

    return resizableDockWidth(contentWidth, CHAT_TILE_ID);
};
